/* eslint-disable max-len */
import { CompanyService } from './company.service';
import { CustomersService } from './customers.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AlertController } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { delay, map, repeat, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Token } from '../interfaces/token';
import { Customer } from '../interfaces/customer';
import { CustomerToken } from '../interfaces/customer-token';
import { User } from '../interfaces/user';
import { Router } from '@angular/router';

export const TOKEN_KEY_USER = 'JWT_USER_EVENT';
export const TOKEN_KEY_CUSTOMER = 'JWT_CUSTOMER_EVENT';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  user: User = null;
  customer: Customer = null;
  token = null;
  id = null;
  userId = null;
  authenticationState = new BehaviorSubject(false);
  authenticated = false;

  constructor(
    private http: HttpClient,
    private helper: JwtHelperService,
    private storage: Storage,
    private customersService: CustomersService,
    private companyService: CompanyService,
    private alertCtrl: AlertController,
    private router: Router
  ) {
    this.storage.create().then(_ => {
      storage.get(TOKEN_KEY_USER).then(token => {
        this.token = token;
      });
    })
    this.checkToken();

  }

  loginWithEmail(email: string, type: string): Observable<{ result: boolean }> {
    return this.http
      .get<any>(`${environment.url}/fn-execute/authenticate1?email=${email}&type=${type}`)
      .pipe(
        map(response => ({
          id: response.id, token: response.token
        }))
      )
      .pipe(
        switchMap(({ id, token }) =>
          this.http
            .get<Token[]>(
              // eslint-disable-next-line max-len
              `${environment.url}/bucket/${environment.buckets.tokens}/data/?relation=true&filter={"token": "${token}", "user._id": "${id}" }`
            )
            .pipe(
              tap(resultToken => {
                if (resultToken[0]) {
                  this.user = resultToken[0].user;
                  this.storage.set(TOKEN_KEY_USER, token);
                  this.token = token;
                  // companyId wird von token company geholt
                  // if (this.user.company !== undefined) {
                  // }
                  this.authenticationState.next(true);
                }
              }),
              map(resultToken => {
                if (resultToken[0]) {
                  return { result: resultToken[0].token === token };
                } else {
                  return { result: false };
                }
              }),
              delay(1000),
              repeat()
            )
        )
      );
  }

  loginWithPassword(email: string, password: string): Observable<{ token: string; user: User }> {
    return this.http
      .get<{ token: string; user: User }>(
        `${environment.url}/fn-execute/authenticateWithPassword?email=${email}&password=${password}`
      )
      .pipe(
        tap(result => {
          if (result.token) {
            this.user = result.user;
            this.storage.set(TOKEN_KEY_USER, result.token);
            this.token = result.token;
            this.authenticationState.next(true);
          }
        })
      );
  }

  authenticate(email: string): Observable<{ id: string; token: string }> {
    return this.http.get<{ id: string; token: string }>(
      `${environment.url}/fn-execute/authenticate?email=${email}`
    );
  }

  checkToken() {
    this.storage.get(TOKEN_KEY_USER).then(token => {
      if (token) {
        const decoded = this.helper.decodeToken(token);
        const isExpired = this.helper.isTokenExpired(token);
        // const isValid = await this.checkTokenValidity(decoded._id, token);
        if (!isExpired) {
          this.token = token;
          this.user = decoded;
          // console.log('USER: ', this.user);
          this.authenticationState.next(true);
        } else {
          this.authenticationState.next(false);
          this.storage.remove(TOKEN_KEY_USER);
        }
      }
      else {
        this.authenticationState.next(false);

      }
    });
  }

  public async getUserToken(): Promise<string> {
    return await this.storage.get(TOKEN_KEY_USER);
  }

  public async getCustomerToken(): Promise<string> {
    return await this.storage.get(TOKEN_KEY_CUSTOMER);
  }

  public async getDecodedUserToken() {
    const decoded = this.helper.decodeToken(await this.getUserToken());

    if (decoded) {
      let company = await this.storage.get(environment.storage.company);
      if (company?._id != decoded.company) {
        this.companyService.getCompanyById(decoded.company).subscribe(x => {
          this.storage.set(environment.storage.company, x);
        });
      }
      return decoded;
    } else {
      return null;
    }
  }

  public async getDecodedCustomerToken() {
    const decoded = this.helper.decodeToken(await this.getCustomerToken());

    if (decoded) {
      return decoded;
    } else {
      return null;
    }
  }

  checkTokenValidity(userId, token) {
    return this.http
      .get<any>(`${environment.url}/fn-execute/verifyToken?token=${token}&user=${userId}`)
      .toPromise();
  }

  async logout() {

    await this.storage.clear();
    this.authenticationState.next(false);
    this.router.navigate(["login-user"])

  }

  isAuthenticated() {
    return this.authenticationState.value;
  }

  showAlert(msg) {
    const alert = this.alertCtrl.create({
      message: msg,
      header: 'Error',
      buttons: ['OK']
    });
    alert.then(a => a.present());
  }

  register(email: string) {
    return this.http
      .get<any>(`${environment.url}/fn-execute/register?email=${email}`)
      .pipe(
        map(response => ({ token: response.token }))
      )
      .pipe(
        switchMap(({ token }) =>
          this.http
            .get<Token[]>(
              // eslint-disable-next-line max-len
              `${environment.url}/bucket/${environment.buckets.register_tokens}/data/?relation=true&filter={"token": "${token}", "email": "${email}" }`
            )
            .pipe(
              map(resultToken => {
                if (resultToken[0]) {
                  return { result: resultToken[0].token === token };
                }
                return { result: false };
              }),
              delay(3000),
              repeat()
            )
        )
      );
  }

  checkTokenCustomer() {
    this.storage.get(TOKEN_KEY_CUSTOMER).then(async token => {
      if (token) {
        const decoded = this.helper.decodeToken(token);
        const isExpired = this.helper.isTokenExpired(token);
        // const isValid = await this.checkTokenValidity(decoded._id, token);
        if (!isExpired) {
          this.token = token;
          this.customer = decoded;
          // console.log('CHECK TOKEN CUSTOMER CUSTOMER: ', this.customer);
          // const companyId = await this.storage.get('company_id_customer');
          this.companyService.getCompanyById(this.token.company).subscribe(async res => {
            // TODO: weiss nicht ob ich das hartcodiert reinmachen kann unter environment.companyCategory
            if (typeof res.category !== 'string') {
              this.storage.set('companyType_customer', res.category.name);
            }
          });
          // TODO: Funktion muss noch geschrieben werden
          this.customersService.getSubscribedCustomers(this.token.company).subscribe(res => {
            let found = false;
            // console.log('RES: ', res);
            for (const r of res) {
              if (r.email === this.customer.email) {
                found = true;
                break;
              }
            }
            if (!found) {
              // console.log('NO PERMISION');
            } else {
              this.authenticationState.next(true);
            }
          });
        } else {
          this.storage.remove(TOKEN_KEY_CUSTOMER);
        }
      }
    });
  }

  loginWithEmailCustomer(email: string): Observable<{ result: boolean }> {
    return this.http
      .post<any>(`${environment.url}/fn-execute/customer-login`, { email })
      .pipe(
        map(response => {
          this.storage.set(TOKEN_KEY_CUSTOMER, response.token);
          // console.log('RESPONSE: ', response);
          //this.authenticationState.next(true);
          return { id: response.id, token: response.token };
        })
      )
      .pipe(
        switchMap(({ id, token }) =>
          this.http
            .get<CustomerToken[]>(
              // eslint-disable-next-line max-len
              `${environment.url}/bucket/${environment.buckets.customer_tokens}/data/?relation=true&filter={"token": "${token}", "customer._id": "${id}" }`
            )
            .pipe(
              tap(resultToken => {
                if (resultToken[0]) {
                  this.customer = resultToken[0].customer;
                  this.storage.set(TOKEN_KEY_CUSTOMER, token);
                  this.token = token;
                  this.authenticationState.next(true);
                }
              }),
              map(resultToken => {
                if (resultToken[0]) {
                  return { result: resultToken[0].token === token };
                }
                return { result: false };
              }),
              delay(3000),
              repeat()
            )
        )
      );
  }
}
